import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube } from 'lucide-react';

const Footer = () => {
  const [logoType, setLogoType] = useState('kolej');
  const [contactInfos, setContactInfos] = useState([]);
  const [settings, setSettings] = useState({});
  const location = useLocation();

  useEffect(() => {
    const savedLogoType = localStorage.getItem('siteLogo') || 'kolej';
    setLogoType(savedLogoType);
    fetchContactInfos();
    fetchSettings();
  }, [location.pathname]);

  const fetchContactInfos = async () => {
    try {
      const response = await fetch('http://localhost/api/pages.php?type=contact', {
        credentials: 'include',
      });
      if (response.ok) {
        const text = await response.text();
        const data = text ? JSON.parse(text) : [];
        setContactInfos(data);
      }
    } catch (error) {
      console.error('Error fetching contact infos:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php', {
        credentials: 'include',
      });
      if (response.ok) {
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const logoSrc = logoType === 'kurs-merkezi' 
    ? (settings.kurs_merkezi_logo || '/kursmerkezi.png')
    : (settings.kolej_logo || '/logo.png');
  const logoAlt = logoType === 'kurs-merkezi' ? 'Kurs Merkezi' : 'Serpil Şahin Koleji';

  // Sosyal medya linkleri
  const socialLinks = {
    facebook: settings.social_facebook || '#',
    twitter: settings.social_twitter || '#',
    instagram: settings.social_instagram || '#',
    youtube: settings.social_youtube || '#',
  };

  const getContactInfo = (type) => {
    return contactInfos.find(info => info.type === type);
  };

  const addressInfo = getContactInfo('address');
  const phoneInfo = getContactInfo('phone');
  const emailInfo = getContactInfo('email');
  const hoursInfo = getContactInfo('hours');
  const workingHours = hoursInfo ? hoursInfo.content : 'Pazartesi - Cuma: 08:00 - 17:00';

  return (
    <footer className="relative bg-gradient-to-br from-red-900 via-red-800 to-red-900 text-white overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(192,160,98,0.1),transparent_70%)]"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <div className="mb-4">
              <img 
                src={logoSrc} 
                alt={logoAlt} 
                className="h-16 w-auto object-contain brightness-0 invert"
                onError={(e) => {
                  // Logo yüklenemezse fallback göster
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center space-x-2" style={{display: 'none'}}>
                <div className="w-10 h-10 rounded-full bg-[#C0A062] flex items-center justify-center">
                <span className="text-xl font-bold text-white">{logoType === 'kurs-merkezi' ? 'K' : 'S'}</span>
              </div>
              <h3 className="text-xl font-bold">{logoType === 'kurs-merkezi' ? 'Kurs Merkezi' : 'Serpil Şahin Koleji'}</h3>
              </div>
            </div>
            <p className="text-red-100 text-sm leading-relaxed">
              Geleceğin liderlerini yetiştiren, prestijli ve güvenilir bir eğitim kurumu. Anaokulu'ndan Lise'ye kadar kaliteli eğitim.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#C0A062]">Hızlı Linkler</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/kolej" className="text-red-100 hover:text-[#C0A062] text-sm transition-colors">
                  Ana Sayfa
                </Link>
              </li>
              <li>
                <Link to="/hakkimizda" className="text-red-100 hover:text-[#C0A062] text-sm transition-colors">
                  Hakkımızda
                </Link>
              </li>
              <li>
                <Link to="/egitim-kademeleri" className="text-red-100 hover:text-[#C0A062] text-sm transition-colors">
                  Eğitim Kademeleri
                </Link>
              </li>
              <li>
                <Link to="/kabul" className="text-red-100 hover:text-[#C0A062] text-sm transition-colors">
                  Kabul ve Kayıt
                </Link>
              </li>
              <li>
                <Link to="/iletisim" className="text-red-100 hover:text-[#C0A062] text-sm transition-colors">
                  İletişim
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#C0A062]">İletişim</h4>
            <ul className="space-y-3">
              {addressInfo && (
                <li className="flex items-start space-x-2">
                  <MapPin size={18} className="text-[#C0A062] mt-1 flex-shrink-0" />
                  <span className="text-red-100 text-sm break-words leading-relaxed flex-1 min-w-0">{addressInfo.content}</span>
                </li>
              )}
              {phoneInfo && (
                <li className="flex items-center space-x-2">
                  <Phone size={18} className="text-[#C0A062] flex-shrink-0" />
                  <span className="text-red-100 text-sm">{phoneInfo.content}</span>
                </li>
              )}
              {emailInfo && (
                <li className="flex items-center space-x-2">
                  <Mail size={18} className="text-[#C0A062] flex-shrink-0" />
                  <span className="text-red-100 text-sm">{emailInfo.content}</span>
                </li>
              )}
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#C0A062]">Bizi Takip Edin</h4>
            <div className="flex space-x-4">
              {socialLinks.facebook && socialLinks.facebook !== '#' && (
                <a
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-white/5 backdrop-blur-sm hover:bg-gradient-to-br hover:from-[#C0A062] hover:to-[#9a7d4a] flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-[#C0A062] hover:scale-110 hover:rotate-6 shadow-lg"
                  aria-label="Facebook"
                >
                  <Facebook size={20} />
                </a>
              )}
              {socialLinks.twitter && socialLinks.twitter !== '#' && (
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-white/5 backdrop-blur-sm hover:bg-gradient-to-br hover:from-[#C0A062] hover:to-[#9a7d4a] flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-[#C0A062] hover:scale-110 hover:rotate-6 shadow-lg"
                  aria-label="X (Twitter)"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              )}
              {socialLinks.instagram && socialLinks.instagram !== '#' && (
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-white/5 backdrop-blur-sm hover:bg-gradient-to-br hover:from-[#C0A062] hover:to-[#9a7d4a] flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-[#C0A062] hover:scale-110 hover:rotate-6 shadow-lg"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
              )}
              {socialLinks.youtube && socialLinks.youtube !== '#' && (
                <a
                  href={socialLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-white/5 backdrop-blur-sm hover:bg-gradient-to-br hover:from-[#C0A062] hover:to-[#9a7d4a] flex items-center justify-center transition-all duration-300 border border-white/10 hover:border-[#C0A062] hover:scale-110 hover:rotate-6 shadow-lg"
                  aria-label="Youtube"
                >
                  <Youtube size={20} />
                </a>
              )}
            </div>
            <div className="mt-6">
              <p className="text-xs text-red-100 mb-2">Çalışma Saatleri:</p>
              <p className="text-sm text-white font-medium">{workingHours}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-red-500/20 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link to="/gizlilik-politikasi" className="text-red-100 hover:text-[#C0A062] transition-colors">
                Gizlilik Politikası
              </Link>
              <span className="text-red-500/50">•</span>
              <Link to="/kullanim-sartlari" className="text-red-100 hover:text-[#C0A062] transition-colors">
                Kullanım Şartları
              </Link>
              <span className="text-red-500/50">•</span>
              <span className="text-red-100 text-sm flex items-center gap-2">
                Tasarım & Geliştirme:
                <a 
                  href="https://aslunox.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                >
                  <img 
                    src="/aslunox.png" 
                    alt="Aslunox" 
                    className="h-8 w-auto object-contain"
                  />
                </a>
              </span>
            </div>
            <div className="backdrop-blur-sm bg-white/5 inline-block px-8 py-4 rounded-full border border-white/10">
              <p className="text-white text-sm font-medium">
                © 2025 Serpil Şahin Koleji. Tüm hakları saklıdır.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;